import { Controller } from "@hotwired/stimulus";

// Manages the trading-card modal: open/close (with Escape + backdrop
// dismissal + body scroll lock), plus the share-button intents to X
// (Twitter), Facebook, and Instagram. Twitter/Facebook use their
// public share-intent URLs and pop up in a sized window. Instagram
// has no public web share API for feed posts, so we prefer the
// browser-native navigator.share() (which on mobile can route to the
// IG app), then fall back to writing the figure URL to the clipboard.
//
// Targets: modal (the fullscreen overlay container).
// Values:  url (the figure's public URL), title (the figure's name).
export default class extends Controller {
  static targets = ["modal"];
  static values = { url: String, title: String };

  connect() {
    this._escHandler = (event) => {
      if (event.key === "Escape" && !this.modalTarget.classList.contains("hidden")) {
        this.close();
      }
    };
  }

  disconnect() {
    document.removeEventListener("keydown", this._escHandler);
    document.body.classList.remove("overflow-hidden");
  }

  open(event) {
    event?.preventDefault();
    this.modalTarget.classList.remove("hidden");
    document.body.classList.add("overflow-hidden");
    document.addEventListener("keydown", this._escHandler);
  }

  close(event) {
    event?.preventDefault();
    this.modalTarget.classList.add("hidden");
    document.body.classList.remove("overflow-hidden");
    document.removeEventListener("keydown", this._escHandler);
  }

  // Backdrop click: only close when the click landed on the modal
  // container itself (not bubbled up from a child).
  backdropClose(event) {
    if (event.target === event.currentTarget) {
      this.close();
    }
  }

  shareTwitter(event) {
    event?.preventDefault();
    const text = `Check out my custom: ${this.titleValue}`;
    const intent = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(this.urlValue)}`;
    this._popup(intent);
  }

  shareFacebook(event) {
    event?.preventDefault();
    const intent = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(this.urlValue)}`;
    this._popup(intent);
  }

  async shareInstagram(event) {
    event?.preventDefault();
    const url = this.urlValue;
    const title = this.titleValue;
    const text = `Check out my custom: ${title}`;

    if (navigator.share) {
      try {
        await navigator.share({ title, text, url });
        return;
      } catch (err) {
        // AbortError = user cancelled the native share sheet; treat
        // as a no-op and skip the clipboard fallback so we don't toast.
        if (err && err.name === "AbortError") return;
      }
    }

    try {
      await navigator.clipboard.writeText(url);
      this._toast("Link copied — paste it into Instagram.");
    } catch (err) {
      window.prompt("Copy this link to share on Instagram:", url);
    }
  }

  _popup(url) {
    const w = 600;
    const h = 480;
    const left = window.screenX + (window.innerWidth - w) / 2;
    const top = window.screenY + (window.innerHeight - h) / 2;
    window.open(
      url,
      "share",
      `noopener,noreferrer,width=${w},height=${h},left=${left},top=${top}`
    );
  }

  _toast(msg) {
    const toast = document.createElement("div");
    toast.className =
      "fixed bottom-6 left-1/2 -translate-x-1/2 z-[100] bg-gray-900 text-white px-4 py-2 rounded shadow-lg text-sm pointer-events-none";
    toast.textContent = msg;
    document.body.appendChild(toast);
    setTimeout(() => toast.remove(), 2500);
  }
}
